import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import overlayFactory from "react-bootstrap-table2-overlay";
import { toast } from "react-toastify";
import { Alert, Card, CardBody, CardTitle, Col, Progress, Row, Spinner } from "reactstrap";
import ControlService from "src/modules/data-protection/apis/ControlService";
import { EMPTY_LIST } from "src/common/constants";
import { formatLegalEntityIdsParam, isTisaxFramework } from "src/modules/data-protection/helpers/Controls";
import FilteringComponent from "./filtering-component";

const filterRowsByLegalEntityIds = (rows, legalEntityIds) => {
  const selectedIds = formatLegalEntityIdsParam(legalEntityIds);

  if (!selectedIds?.length) {
    return rows;
  }

  const selectedIdSet = new Set(selectedIds.map(String));

  return rows.filter((row) =>
    selectedIdSet.has(String(row.legal_entity_id))
  );
};

const renderMaturityDots = (value) => {
  const score = Number(value || 0);
  const filled = Math.max(0, Math.min(5, Math.round(score)));

  return (
    <span className="d-inline-flex align-items-center gap-1">
      {Array.from({ length: 5 }).map((_, index) => (
        <span
          key={index}
          aria-hidden="true"
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: index < filled ? "#30B24A" : "#D6D9DE",
            display: "inline-block",
          }}
        />
      ))}
    </span>
  );
};

const getReadinessColor = (value) => {
  const readiness = Number(value || 0);
  if (readiness >= 75) return "success";
  if (readiness >= 50) return "warning";
  return "danger";
};

const LegalEntityComparisonSection = ({
  t,
  frameworkType,
  queryEnabled = false,
}) => {
  const showMaturity = isTisaxFramework(frameworkType);
  const [tableFilters, setTableFilters] = useState({
    legalEntityIds: undefined,
  });

  const legalEntitiesQuery = useQuery({
    queryKey: [
      "data-protection-controls-dashboard-legal-entities",
      frameworkType,
    ],
    queryFn: async () => {
      const service = ControlService.getInstance();
      return await service.dashboardLegalEntities({
        type: frameworkType,
      });
    },
    enabled: queryEnabled,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: () => {
      toast(t("An error occurred while fetching legal entity comparison data."), {
        type: "error",
      });
    },
  });

  const allRows = legalEntitiesQuery.data?.legal_entity_comparison || [];

  const rows = useMemo(
    () => filterRowsByLegalEntityIds(allRows, tableFilters.legalEntityIds),
    [allRows, tableFilters.legalEntityIds]
  );

  const tableColumns = useMemo(() => {
    const columns = [
      {
        dataField: "legal_entity",
        text: t("Legal Entity"),
        sort: false,
      },
      {
        dataField: "frameworks",
        text: t("Frameworks"),
        sort: false,
        formatter: (cell) => cell || 0,
      },
      {
        dataField: "total_controls",
        text: t("Total Controls"),
        sort: false,
        formatter: (cell) => cell || 0,
      },
      {
        dataField: "applicable_percent",
        text: t("Applicable"),
        sort: false,
        formatter: (cell) => `${cell || 0}%`,
      },
    ];

    if (showMaturity) {
      columns.push({
        dataField: "avg_maturity",
        text: t("Avg Maturity Level"),
        sort: false,
        formatter: (cell) => (
          <div className="d-flex align-items-center gap-2">
            <span>{cell || 0}</span>
            {renderMaturityDots(cell)}
          </div>
        ),
      });
    }

    columns.push({
      dataField: "readiness",
      text: t("Readiness"),
      sort: false,
      style: { minWidth: 140 },
      formatter: (cell) => (
        <div className="d-flex align-items-center gap-2">
          <span>{cell || 0}%</span>
          <Progress
            value={cell || 0}
            color={getReadinessColor(cell)}
            style={{ height: 6, width: 90 }}
          />
        </div>
      ),
    });

    return columns;
  }, [t, showMaturity]);

  const handleFilterChange = useCallback((newFilters) => {
    setTableFilters((currentFilters) => ({
      ...currentFilters,
      ...newFilters,
    }));
  }, []);

  const paginationOptions = useMemo(
    () => ({
      hideSizePerPage: false,
      hidePageListOnlyOnePage: false,
      sizePerPageList: [
        { text: "5th", value: 5 },
        { text: "10th", value: 10 },
        { text: "All", value: rows.length || 1 },
      ],
    }),
    [rows.length]
  );

  const NoDataIndication = () =>
    legalEntitiesQuery.isFetched && !rows.length ? (
      <div className="alert m-0" role="alert">
        <p className="mb-0 text-center">{t(EMPTY_LIST)}</p>
      </div>
    ) : null;

  return (
    <Row className="mb-4">
      <Col lg="12">
        <Card className="card_box_Shadow">
          <CardBody>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <CardTitle className="mb-0">{t("Legal Entity Comparison")}</CardTitle>
              <FilteringComponent
                queryEnabled={queryEnabled}
                onChange={handleFilterChange}
              />
            </div>

            {legalEntitiesQuery.isError ? (
              <Alert color="danger" className="mb-0">
                {t("Unable to load legal entity comparison data at this time.")}
              </Alert>
            ) : (
              <div
                className="table-responsive dissco-latest-cases"
                style={{ overflowX: "hidden", maxWidth: "100%" }}
              >
                <BootstrapTable
                  keyField="legal_entity_id"
                  responsive
                  bordered={false}
                  data={rows}
                  columns={tableColumns}
                  classes="table tpdd-table mb-4 align-middle"
                  headerWrapperClasses="thead-light"
                  loading={
                    legalEntitiesQuery.isFetching || legalEntitiesQuery.isLoading
                  }
                  overlay={overlayFactory({
                    spinner: (
                      <Spinner animation="border" variant="primary" size="md" />
                    ),
                    text: t("Loading"),
                  })}
                  noDataIndication={() => <NoDataIndication />}
                  pagination={paginationFactory(paginationOptions)}
                  options={{
                    hideSizePerPage: true,
                  }}
                />
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default LegalEntityComparisonSection;
